import { useCallback, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { uploadData } from 'aws-amplify/storage';
import { captureError } from '../../../utils/sentryHelpers';
import {
  MEDIATHEK_IMAGE_TYPE,
  buildUploadKey,
  isRetryableUploadError,
  parseUploadCompanyImageResponse,
  publicObjectUrl,
  validateImageFile,
  validateMediaFile,
} from '../../../utils/mediathek';
import { FINALIZE_MEDIATHEK_UPLOAD, UPLOAD_COMPANY_IMAGE } from '../mediathekGraphql';

const client = generateClient();

/** finalizeMediathekUpload is idempotent: retry timeouts / network errors (3 attempts in total). */
const FINALIZE_ATTEMPTS = 3;
const FINALIZE_RETRY_DELAY_MS = 3000;

const IDLE = { busy: false, progress: 0, phase: null, error: null };

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function randomUuid() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  // Fallback for non-secure contexts: RFC 4122 v4 from getRandomValues
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:.+;base64,/, ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

function errorMessage(error, fallback) {
  return error?.errors?.[0]?.message || error?.message || fallback;
}

/**
 * Uploads for the Mediathek forms.
 *
 * - uploadImage(file, slot): jpg/jpeg/png <= 3 MiB (IMAGE_UPLOAD_RULES; base64 in the GraphQL
 *   request must stay below the AppSync/Lambda payload limits), base64 -> uploadCompanyImage
 *   (imageType "mediathekImage", stored under mediathekImages/) -> { fileName, alt: '' }
 * - uploadMedia(file, kind): kind "audio" | "video"; Amplify Storage upload to
 *   public/mediathekUploads/<uuid>-<name> (progress), then finalizeMediathekUpload
 *   moves it to iec-cp-public -> { s3Path, url, publicUrl, fileName, type }
 *
 * Both resolve to null on failure; the error (English message or the backend's
 * message) is available in `uploads[slot].error` and has been sent to Sentry.
 *
 * @param {string} [component] component name for Sentry tags
 */
export default function useMediathekUpload(component = 'useMediathekUpload') {
  const [uploads, setUploads] = useState({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const patch = useCallback((slot, values) => {
    if (!mounted.current) return;
    setUploads((prev) => ({ ...prev, [slot]: { ...IDLE, ...prev[slot], ...values } }));
  }, []);

  const getUpload = useCallback((slot) => uploads[slot] || IDLE, [uploads]);

  const resetUpload = useCallback((slot) => {
    if (!mounted.current) return;
    setUploads((prev) => ({ ...prev, [slot]: IDLE }));
  }, []);

  const uploadImage = useCallback(async (file, slot = 'image') => {
    const invalid = validateImageFile(file);
    if (invalid) {
      patch(slot, { ...IDLE, error: invalid });
      return null;
    }
    patch(slot, { busy: true, progress: 0, phase: 'upload', error: null });
    try {
      const imageBuffer = await readAsBase64(file);
      const response = await client.graphql({
        query: UPLOAD_COMPANY_IMAGE,
        variables: { imageBuffer, imageType: MEDIATHEK_IMAGE_TYPE },
      });
      const fileName = parseUploadCompanyImageResponse(response?.data?.uploadCompanyImage);
      patch(slot, { busy: false, progress: 100, phase: null, error: null });
      return { fileName, alt: '' };
    } catch (error) {
      console.error('Error uploading Mediathek image:', error);
      captureError(error, {
        feature: 'mediathek',
        operation: 'upload-image',
        context: { slot, fileName: file?.name, size: file?.size },
        tags: { component },
      });
      patch(slot, { busy: false, progress: 0, phase: null, error: errorMessage(error, 'Image upload failed') });
      return null;
    }
  }, [patch, component]);

  const finalizeUpload = useCallback(async (input) => {
    for (let attempt = 1; ; attempt += 1) {
      try {
        const response = await client.graphql({ query: FINALIZE_MEDIATHEK_UPLOAD, variables: { input } });
        return response?.data?.finalizeMediathekUpload || null;
      } catch (error) {
        // The final failure is reported by uploadMedia(); retried attempts are reported here
        if (attempt >= FINALIZE_ATTEMPTS || !isRetryableUploadError(error)) throw error;
        console.warn(`finalizeMediathekUpload attempt ${attempt} failed, retrying`, error);
        captureError(error, {
          feature: 'mediathek',
          operation: 'finalize-upload-retry',
          context: { attempt, s3Path: input?.s3Path, kind: input?.kind },
          tags: { component },
        });
        await sleep(FINALIZE_RETRY_DELAY_MS);
      }
    }
  }, [component]);

  const uploadMedia = useCallback(async (file, kind) => {
    const invalid = validateMediaFile(file, kind);
    if (invalid) {
      patch(kind, { ...IDLE, error: invalid });
      return null;
    }
    const contentType = file.type || 'application/octet-stream';
    let key = null;
    patch(kind, { busy: true, progress: 0, phase: 'upload', error: null });
    try {
      key = buildUploadKey(randomUuid(), file.name);
      await uploadData({
        key,
        data: file,
        options: {
          accessLevel: 'guest',
          contentType,
          onProgress: ({ transferredBytes, totalBytes }) => {
            const total = totalBytes || file.size;
            if (total) patch(kind, { progress: Math.min(99, Math.round((transferredBytes / total) * 100)) });
          },
        },
      }).result;

      patch(kind, { progress: 99, phase: 'finalize' });
      const result = await finalizeUpload({ s3Path: `public/${key}`, fileName: file.name, contentType, kind });
      if (!result?.s3Path) throw new Error('The upload could not be finalized');
      const url = result.url || publicObjectUrl(result.s3Path);
      patch(kind, { busy: false, progress: 100, phase: null, error: null });
      return {
        s3Path: result.s3Path,
        url,
        publicUrl: url,
        fileName: result.fileName || file.name,
        type: result.type || contentType,
      };
    } catch (error) {
      console.error(`Error uploading Mediathek ${kind}:`, error);
      captureError(error, {
        feature: 'mediathek',
        operation: `upload-${kind}`,
        context: { kind, key, fileName: file?.name, size: file?.size, contentType },
        tags: { component },
      });
      patch(kind, { busy: false, progress: 0, phase: null, error: errorMessage(error, 'Upload failed') });
      return null;
    }
  }, [patch, finalizeUpload, component]);

  return { uploads, getUpload, resetUpload, uploadImage, uploadMedia };
}
